import { useRef, useCallback } from 'react';

/**
 * useSingingBowl — Synthesize a singing bowl tone using the Web Audio API.
 * Zero dependencies, no audio files needed.
 *
 * A singing bowl is: multiple sine oscillators at harmonic frequencies,
 * exponential amplitude decay, slight detuning for organic warmth.
 */
export default function useSingingBowl() {
  const audioCtxRef = useRef(null);

  const getAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      try {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch {
        return null;
      }
    }
    // Resume if suspended (browser autoplay policy)
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const strike = useCallback((volume = 0.3) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const fundamentalFreq = 220; // A3 — warm, grounding tone
    const harmonics = [1, 2.76, 5.04, 8.47]; // Bowl-like overtone series
    const gains = [1, 0.6, 0.3, 0.15];
    const decays = [4, 3, 2, 1.5]; // Higher harmonics decay faster

    harmonics.forEach((ratio, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.frequency.value = fundamentalFreq * ratio;
      osc.type = 'sine';

      gain.gain.setValueAtTime(gains[i] * volume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + decays[i]);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + decays[i]);
    });
  }, [getAudioContext]);

  const doubleStrike = useCallback((volume = 0.25) => {
    strike(volume);
    setTimeout(() => strike(volume * 0.7), 600);
  }, [strike]);

  const cleanup = useCallback(() => {
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  }, []);

  return { strike, doubleStrike, cleanup };
}
